import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download, Trash2, Loader2, AlertTriangle, Link2, Copy, Columns3 } from 'lucide-react';
import { toast } from 'sonner';
import { useApi, useAuth } from '@/contexts/AuthContext';

const C = { navy: '#0D1B2A', slate: '#5C6B7A', green: '#1D9E75', amber: '#E8A020', red: '#E24B4A', blue: '#1A4C7C' };
const SEV = { high: C.red, medium: C.amber, low: C.slate };
const bar = (pct) => (pct >= 95 ? C.green : pct >= 75 ? C.amber : C.red);
const n = (v) => (v || 0).toLocaleString();

function Chip({ text, color, testid }) {
  return <span data-testid={testid} className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide"
    style={{ background: color + '15', color }}>{text}</span>;
}

function Metric({ label, value, suffix }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider" style={{ color: C.slate }}>{label}</p>
      <p className="text-sm font-mono font-semibold" style={{ color: C.navy }}>{value}{suffix}</p>
    </div>
  );
}

function FieldTable({ entity, fields }) {
  return (
    <table className="w-full text-xs" data-testid={`field-table-${entity}`}>
      <thead><tr className="text-left text-[10px] uppercase" style={{ color: C.slate }}>
        <th className="py-1 pr-2">Canonical field</th><th className="py-1 pr-2">Source</th>
        <th className="py-1 pr-2">Populated</th><th className="py-1 pr-2 w-32">Completeness</th>
        <th className="py-1 pr-2">Distinct</th><th className="py-1 pr-2">Format failures</th>
        <th className="py-1">Most common values</th>
      </tr></thead>
      <tbody>
        {fields.map(f => (
          <tr key={f.field} className="border-t" style={{ borderColor: 'rgba(136,150,168,0.12)' }}
            data-testid={`field-row-${entity}-${f.field.replace(/\./g, '-')}`}>
            <td className="py-1 pr-2" style={{ color: C.navy }}>
              {f.label}{f.required && <span className="ml-1 text-[9px] font-bold" style={{ color: C.red }}>REQ</span>}
            </td>
            <td className="py-1 pr-2 font-mono text-[10px]" style={{ color: f.mapped === false ? C.red : C.slate }}>
              {f.mapped === false ? 'not mapped' : f.source_field}
            </td>
            <td className="py-1 pr-2 font-mono" style={{ color: C.slate }}>{n(f.present)}</td>
            <td className="py-1 pr-2">
              <div className="flex items-center gap-1.5">
                <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(136,150,168,0.15)' }}>
                  <div className="h-full rounded-full" style={{ width: `${f.completeness_pct}%`, background: bar(f.completeness_pct) }} />
                </div>
                <span className="font-mono text-[10px]" style={{ color: bar(f.completeness_pct) }}>{f.completeness_pct}%</span>
              </div>
            </td>
            <td className="py-1 pr-2 font-mono" style={{ color: C.slate }}>{n(f.distinct)}</td>
            <td className="py-1 pr-2 font-mono" style={{ color: f.anomalies ? C.red : C.slate }}>
              {f.anomalies ? `${n(f.anomalies)} (${f.anomaly_pct}%)` : '—'}
            </td>
            <td className="py-1 text-[10px]" style={{ color: C.slate }}>
              {(f.top_values || []).slice(0, 3).map(v => `${v.value} (${n(v.count)})`).join(' · ') || '—'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function EntityProfile({ p }) {
  const [open, setOpen] = useState(false);
  const r = p.readiness || {};
  return (
    <Card className="rounded-md bg-white p-4" data-testid={`entity-card-${p.entity}`}
      style={{ border: '1px solid rgba(136,150,168,0.12)', borderLeft: `3px solid ${bar(r.score || 0)}` }}>
      <div className="flex items-start justify-between gap-4 mb-2">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>{p.label}</h3>
            <Chip testid={`entity-score-${p.entity}`} text={`readiness ${r.score ?? '—'}`} color={bar(r.score || 0)} />
            <Chip text={p.basis === 'sample' ? `sampled ${n(p.profiled_rows)} of ${n(p.rows)}` : `all ${n(p.rows)} rows`} color={C.blue} />
            {!!p.rehearsal_rows_excluded && <Chip text={`${n(p.rehearsal_rows_excluded)} rehearsal rows excluded`} color={C.slate} />}
          </div>
          <p className="text-[11px] mt-1" style={{ color: C.slate }}>{p.note}{p.collection ? ` · ${p.collection}` : ''}</p>
        </div>
        <div className="grid grid-cols-3 gap-4 shrink-0">
          <Metric label="Required fields" value={r.required_completeness_pct ?? '—'} suffix="%" />
          <Metric label="Format conformance" value={r.format_conformance_pct ?? '—'} suffix="%" />
          <Metric label="Referential integrity" value={r.referential_integrity_pct ?? '—'} suffix="%" />
        </div>
      </div>
      {r.basis && <p className="text-[10px] mb-2" style={{ color: C.slate }}>Readiness is computed as {r.basis}.</p>}

      {!!(p.unmapped_required_fields || []).length && (
        <p className="text-[11px] mb-1.5 p-2 rounded-sm" style={{ background: C.red + '10', color: '#B03230' }}
          data-testid={`unmapped-required-${p.entity}`}>
          <Columns3 className="w-3 h-3 inline mr-1" />No source column maps to: <b>{p.unmapped_required_fields.join(', ')}</b> — required on the canonical shape.
        </p>
      )}
      {!!(p.unmapped_source_columns || []).length && (
        <p className="text-[11px] mb-1.5" style={{ color: C.slate }} data-testid={`unmapped-columns-${p.entity}`}>
          Source columns with no canonical home ({p.unmapped_source_columns.length}): {p.unmapped_source_columns.slice(0, 12).join(', ')}
        </p>
      )}

      {!!(p.duplicates || []).length && (
        <div className="mb-1.5" data-testid={`duplicates-${p.entity}`}>
          {p.duplicates.map(d => (
            <p key={d.key} className="text-[11px]" style={{ color: C.navy }}>
              <Copy className="w-3 h-3 inline mr-1" style={{ color: C.red }} />
              <b>{n(d.groups)}</b> duplicated <span className="font-mono">{d.key}</span> values across <b>{n(d.rows)}</b> rows — e.g. {d.examples.slice(0, 3).join(', ')}
            </p>
          ))}
        </div>
      )}
      {!!(p.orphans || []).length && (
        <div className="mb-1.5" data-testid={`orphans-${p.entity}`}>
          {p.orphans.map(o => (
            <p key={o.field} className="text-[11px]" style={{ color: C.navy }}>
              <Link2 className="w-3 h-3 inline mr-1" style={{ color: o.missing ? C.red : C.green }} />
              {o.label}: <b>{n(o.missing)}</b> of {n(o.referencing)} references point at nothing ({o.pct}%)
              {o.missing ? ` — e.g. ${o.examples.slice(0, 2).join(', ')}` : ''}
            </p>
          ))}
        </div>
      )}
      {p.orphan_note && <p className="text-[10px] mb-1.5" style={{ color: C.slate }}>{p.orphan_note}</p>}

      <Button size="sm" variant="ghost" onClick={() => setOpen(o => !o)} className="text-[11px] h-6 px-2 rounded-sm"
        style={{ color: C.blue }} data-testid={`fields-toggle-${p.entity}`}>
        {open ? 'Hide' : 'Show'} {(p.fields || []).length} profiled fields
      </Button>
      {open && <div className="mt-2 overflow-x-auto"><FieldTable entity={p.entity} fields={p.fields || []} /></div>}
    </Card>
  );
}

export function MigrationProfileDetail({ run, onDeleted }) {
  const api = useApi();
  const { user } = useAuth();
  const isAdmin = ['platform_owner', 'mga_admin'].includes(user?.role);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const t = run.totals || {};

  const download = async () => {
    try {
      const res = await api.get(`/migration/profile/runs/${run.run_id}/lineage.csv`, { responseType: 'blob' });
      if (!(res.headers?.['content-type'] || '').includes('csv')) throw new Error('not a csv');
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = `lineage-register-${run.run_id.slice(0, 8)}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Lineage register exported');
    } catch { toast.error('Export failed'); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/migration/profile/runs/${run.run_id}`);
      toast.success('Run deleted — no client rows were ever stored');
      onDeleted();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
    setBusy(false);
  };

  return (
    <div className="space-y-3" data-testid="run-detail">
      <Card className="rounded-md bg-white p-4" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Chip testid="run-scope-chip" text={run.scope === 'extract' ? 'client extract' : 'platform register'}
                color={run.scope === 'extract' ? C.amber : C.blue} />
              <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>{run.source}</h3>
            </div>
            <p className="text-[11px]" style={{ color: C.slate }}>
              {run.finished_at?.slice(0, 19).replace('T', ' ')} · {run.duration_ms}ms · {run.run_by?.email || '—'}
              {run.client ? ` · client: ${run.client}` : ''}
            </p>
            {run.retention && <p className="text-[10px] mt-1" style={{ color: C.green }}>{run.retention}</p>}
          </div>
          <div className="flex items-center gap-4">
            <Metric label="Rows" value={n(t.rows)} />
            <Metric label="Readiness" value={t.readiness} />
            <Metric label="Findings" value={`${n(t.issues)}`} suffix={t.high ? ` (${t.high} high)` : ''} />
            <div className="flex gap-1.5">
              <Button size="sm" variant="outline" onClick={download} className="text-[11px] rounded-sm h-7"
                data-testid="lineage-csv-btn"><Download className="w-3 h-3 mr-1" />Lineage CSV</Button>
              {isAdmin && (confirm ? (
                <Button size="sm" onClick={remove} disabled={busy} className="text-[11px] rounded-sm h-7"
                  style={{ background: C.red }} data-testid="confirm-delete-btn">
                  {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Confirm delete'}
                </Button>
              ) : (
                <Button size="sm" variant="outline" onClick={() => setConfirm(true)} className="text-[11px] rounded-sm h-7"
                  style={{ color: C.red }} data-testid="delete-run-btn"><Trash2 className="w-3 h-3" /></Button>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {!!(run.profiles || []).some(p => (p.issues || []).length) && (
        <Card className="rounded-md bg-white p-4" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="findings-card">
          <h3 className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: C.slate }}>
            What has to be decided before a record moves
          </h3>
          <div className="space-y-1">
            {(run.profiles || []).flatMap(p => p.issues || [])
              .sort((a, b) => (a.severity === b.severity ? b.count - a.count : a.severity === 'high' ? -1 : 1))
              .slice(0, 40)
              .map((i, idx) => (
                <div key={idx} className="flex items-start gap-2 text-[11px]" data-testid={`issue-row-${idx}`}>
                  <Chip text={i.kind} color={SEV[i.severity] || C.slate} />
                  <span style={{ color: C.navy }}><b>{i.headline}</b> <span style={{ color: C.slate }}>{i.detail}</span></span>
                </div>
              ))}
          </div>
        </Card>
      )}

      {(run.profiles || []).map(p => p.error
        ? <Card key={p.entity} className="rounded-md bg-white p-3 text-[11px]" style={{ color: C.red, border: '1px solid rgba(136,150,168,0.12)' }}>
          <AlertTriangle className="w-3 h-3 inline mr-1" />{p.label} could not be profiled: {p.error}
        </Card>
        : <EntityProfile key={p.entity} p={p} />)}
    </div>
  );
}
